import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  Checkbox,
  Autocomplete,
  Stack
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';
import PurchaseOrderList from './PurchaseOrderList';
import PurchaseOrderDetail from './PurchaseOrderDetail';
import PurchaseOrderForm from './PurchaseOrderForm';
import StoreWorkOrderRequests from './StoreWorkOrderRequests';
import { useServerSideAutocomplete } from '../../hooks/useServerSideAutocomplete';
import { inventoryOptionLabel } from '../../utils/partMasterLabel';
import { normalizePartNumber } from '../../utils/partNumber';
import {
  fetchInventoryByPartNumber,
  inventoryAutofillFields
} from '../../utils/inventoryPartLookup';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/** Sub-tabs inside ProcurementModule */
const TAB_REORDER = 0;
const TAB_INDEPENDENT_PO = 1;
const TAB_WO_REQUIREMENT = 2;
const TAB_PURCHASE_ORDERS = 3;
const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'ORDERED', 'DELIVERED', 'CANCELLED'];
const URGENCY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const PO_PRIORITY_FROM_URGENCY = {
  CRITICAL: 'CRITICAL',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'MEDIUM'
};

/** Prefill PurchaseOrderForm from Independent Planning reorder request(s) — same path as WO Requirement. */
function buildPoPrefillFromIndependentRequests(selectedRequests) {
  const rows = Array.isArray(selectedRequests) ? selectedRequests.filter(Boolean) : [];
  if (!rows.length) return null;

  const lineItems = rows.map((request, index) => ({
    lineNumber: index + 1,
    partNumber: normalizePartNumber(request.partNumber),
    description: String(request.description || '').trim(),
    quantity: Number(request.requestedQuantity) || 1,
    unit: 'EACH',
    remarks: String(request.notes || 'Independent planning procurement request').trim()
  }));
  if (!lineItems.length) return null;

  const first = rows[0];
  const aircraftMongoId = first.requestedAircraftId?._id || first.requestedAircraftId || '';
  const aircraftRegistration =
    first.requestedAircraftLabel || first.requestedAircraftId?.registration || (aircraftMongoId ? '' : 'GEN');
  const isGen = !aircraftMongoId || aircraftRegistration === 'GEN';
  const notes = rows
    .map((request) => request.notes)
    .filter(Boolean)
    .join('\n') || 'Independent planning procurement request (no WO link)';
  const priority = PO_PRIORITY_FROM_URGENCY[first.urgency] || 'MEDIUM';

  return {
    orderTo: first.supplier?._id || first.supplier || '',
    billTo: '',
    shipTo: '',
    operatorId: '',
    aircraftId: isGen ? 'GEN' : aircraftMongoId,
    aircraftRegistration: isGen ? 'GEN' : aircraftRegistration,
    aircraftModelVariant: '',
    aircraftSerialNumber: '',
    expectedDeliveryDate: '',
    priority,
    notes,
    lineItems
  };
}

const PO_DEEP_LINK_STATUSES = [
  'DRAFT',
  'PENDING_STORE_APPROVAL',
  'PENDING_AM_APPROVAL',
  'ISSUED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CLOSED',
  'CANCELLED'
];

const ProcurementModule = ({ deepLinkPoForm = null, onDeepLinkPoFormConsumed }) => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [poPrefill, setPoPrefill] = useState(null);
  const [poFormSessionKey, setPoFormSessionKey] = useState(0);
  const appliedPoDeepLinkNonceRef = useRef(null);
  const appliedPoStatusFromUrlRef = useRef(false);

  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });
  const [procurementTab, setProcurementTab] = useState(0);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  useLayoutEffect(() => {
    if (appliedPoStatusFromUrlRef.current) return;
    const raw = searchParams.get('poStatus');
    if (!raw || !PO_DEEP_LINK_STATUSES.includes(raw)) return;
    setPoListFilters((f) => ({ ...f, status: raw }));
    setProcurementTab(TAB_PURCHASE_ORDERS);
    appliedPoStatusFromUrlRef.current = true;
  }, [searchParams]);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const [poView, setPoView] = useState('list'); // 'list' | 'detail' | 'form'
  const [selectedPoId, setSelectedPoId] = useState(null);
  const [poListFilters, setPoListFilters] = useState({ status: 'ALL', supplierId: '', aircraftId: '', search: '', partNumber: '' });
  const [requests, setRequests] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: 'ALL', urgency: 'ALL' });
  const [independentFilters, setIndependentFilters] = useState({ search: '', status: 'ALL', urgency: 'ALL' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createPoFromRequestsOpen, setCreatePoFromRequestsOpen] = useState(false);
  const [createPoFromRequestsPayload, setCreatePoFromRequestsPayload] = useState({
    operatorId: '',
    aircraftId: '',
    billTo: '',
    shipTo: '',
    expectedDeliveryDate: '',
    priority: 'MEDIUM',
    notes: ''
  });
  const PO_PRIORITY_OPTIONS = [
    { value: 'CRITICAL', label: 'AOG (Aircraft On Ground) – Critical' },
    { value: 'HIGH', label: 'Rush (Urgent) – High' },
    { value: 'MEDIUM', label: 'Normal (Regular/Routine) – Medium' }
  ];
  const [aircraft, setAircraft] = useState([]);
  const [operators, setOperators] = useState([]);
  const [billToAgencies, setBillToAgencies] = useState([]);
  const [shipToAgencies, setShipToAgencies] = useState([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [formData, setFormData] = useState({
    partNumber: '',
    description: '',
    requestedQuantity: 1,
    urgency: 'MEDIUM',
    supplier: '',
    linkedMaintenanceId: '',
    notes: ''
  });
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState(null);
  const inventoryBuildParams = useCallback((searchTerm, pageSize) => ({
    category: 'PART',
    includeArchived: false,
    pageSize,
    page: 1,
    ...(searchTerm.trim() ? { search: searchTerm.trim() } : {})
  }), []);
  const inventoryExtractData = useCallback(
    (response) => response.data?.items || response.data || [],
    []
  );
  const inventoryGetItemEndpoint = useCallback((id) => `/api/store/inventory/${id}`, []);
  const {
    options: inventoryOptions,
    loading: inventoryLoading,
    searchInput: inventorySearchInput,
    handleInputChange: handleInventoryInputChange
  } = useServerSideAutocomplete({
    apiEndpoint: '/api/store/inventory',
    buildParams: inventoryBuildParams,
    extractData: inventoryExtractData,
    selectedValue: selectedInventoryItemId,
    getItemEndpoint: inventoryGetItemEndpoint,
    minSearchLength: 2,
    enabled: dialogOpen
  });
  const canManage = [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const canCreatePo = [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SUPER_ADMIN].includes(user?.role);
  const [updatingRequestId, setUpdatingRequestId] = useState(null);
  const [creatingPoFromRequests, setCreatingPoFromRequests] = useState(false);
  const reorderVisibleRequests = useMemo(
    () => requests.filter((request) => request.createdFrom !== 'PLANNING_NON_WO'),
    [requests]
  );
  const independentVisibleRequests = useMemo(
    () =>
      requests
        .filter((request) => request.createdFrom === 'PLANNING_NON_WO')
        .slice()
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [requests]
  );

  useLayoutEffect(() => {
    const nonce = deepLinkPoForm?.nonce;
    const initialData = deepLinkPoForm?.initialData;
    if (nonce == null || !initialData) return;
    if (appliedPoDeepLinkNonceRef.current === nonce) return;
    appliedPoDeepLinkNonceRef.current = nonce;
    setProcurementTab(TAB_PURCHASE_ORDERS);
    setSelectedPoId(null);
    setPoView('form');
    setPoPrefill(initialData);
    setPoFormSessionKey((k) => k + 1);
    onDeepLinkPoFormConsumed?.();
  }, [deepLinkPoForm, onDeepLinkPoFormConsumed]);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await axios.get('/api/store/reorder-requests');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to load reorder requests', error);
      if (error.response?.status !== 429) {
        showToast('Failed to load reorder requests', 'error');
      }
    }
  }, [showToast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (createPoFromRequestsOpen) {
      const load = async () => {
        try {
          const [ac, bill, ship, op] = await Promise.all([
            axios.get('/api/aircraft'),
            axios.get('/api/master-data/agencies', { params: { isActive: 'true', type: 'BILL_TO' } }),
            axios.get('/api/master-data/agencies', { params: { isActive: 'true', type: 'SHIP_TO' } }),
            axios.get('/api/master-data/operators', { params: { isActive: 'true' } })
          ]);
          setAircraft(Array.isArray(ac.data) ? ac.data : (ac.data?.aircraft || ac.data?.data || []));
          setBillToAgencies(bill.data || []);
          setShipToAgencies(ship.data || []);
          setOperators(op.data || []);
        } catch (e) {
          console.error(e);
        }
      };
      load();
    }
  }, [createPoFromRequestsOpen]);

  const selectedRequests = useMemo(
    () => requests.filter((r) => selectedRequestIds.includes(r._id)),
    [requests, selectedRequestIds]
  );
  const selectedSupplierId = useMemo(() => {
    if (selectedRequests.length === 0) return null;
    const id = selectedRequests[0].supplier?._id?.toString?.() || selectedRequests[0].supplier;
    if (!id) return null;
    const allSame = selectedRequests.every(
      (r) => (r.supplier?._id?.toString?.() || r.supplier) === id
    );
    return allSame ? id : null;
  }, [selectedRequests]);
  const canCreatePoFromSelection = selectedRequestIds.length > 0 && selectedSupplierId != null;
  const selectedIndependentRequests = useMemo(
    () =>
      selectedRequests.filter(
        (request) => request.createdFrom === 'PLANNING_NON_WO' && request.status === 'APPROVED'
      ),
    [selectedRequests]
  );
  const canCreateIndependentPoFromSelection =
    canCreatePo &&
    selectedIndependentRequests.length > 0 &&
    selectedIndependentRequests.length === selectedRequestIds.length;

  const openPoFormWithPrefill = useCallback((prefill) => {
    if (!prefill) {
      showToast('Nothing to order for the selected request(s).', 'error');
      return;
    }
    setSelectedPoId(null);
    setPoPrefill(prefill);
    setPoFormSessionKey((k) => k + 1);
    setPoView('form');
    setProcurementTab(TAB_PURCHASE_ORDERS);
  }, [showToast]);

  const handleCreateIndependentPo = useCallback(
    (requestsToOrder) => {
      const rows = Array.isArray(requestsToOrder) ? requestsToOrder : [requestsToOrder];
      const approved = rows.filter((request) => request?.status === 'APPROVED');
      if (!approved.length) {
        showToast('Only AM-approved independent requests can create a PO.', 'error');
        return;
      }
      openPoFormWithPrefill(buildPoPrefillFromIndependentRequests(approved));
    },
    [openPoFormWithPrefill, showToast]
  );

  const handleCreatePoFromRequests = async () => {
    if (!createPoFromRequestsPayload.operatorId) {
      showToast('Operator is required', 'error');
      return;
    }
    if (!createPoFromRequestsPayload.aircraftId) {
      showToast('Aircraft or GEN is required', 'error');
      return;
    }
    setCreatingPoFromRequests(true);
    try {
      const rawDate = createPoFromRequestsPayload.expectedDeliveryDate;
      const expectedDeliveryDateISO = rawDate
        ? (displayTimezone ? displayDateTimeToUTC(rawDate, '00:00', displayTimezone) ?? rawDate : rawDate)
        : undefined;
      const res = await axios.post('/api/store/purchase-orders/from-reorder-requests', {
        reorderRequestIds: selectedRequestIds,
        operatorId: createPoFromRequestsPayload.operatorId,
        aircraftId: createPoFromRequestsPayload.aircraftId,
        billTo: createPoFromRequestsPayload.billTo || undefined,
        shipTo: createPoFromRequestsPayload.shipTo || undefined,
        expectedDeliveryDate: expectedDeliveryDateISO,
        priority: createPoFromRequestsPayload.priority || 'MEDIUM',
        notes: createPoFromRequestsPayload.notes || undefined
      });
      showToast('Purchase order created', 'success');
      setCreatePoFromRequestsOpen(false);
      setSelectedRequestIds([]);
      setCreatePoFromRequestsPayload({ operatorId: '', aircraftId: '', billTo: '', shipTo: '', expectedDeliveryDate: '', priority: 'MEDIUM', notes: '' });
      setProcurementTab(TAB_PURCHASE_ORDERS);
      setPoView('detail');
      setSelectedPoId(res.data?._id);
    } catch (error) {
      showToast(getErrorMessage(error, 'Failed to create purchase order.'), 'error');
    } finally {
      setCreatingPoFromRequests(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/api/master-data/agencies', {
        params: { isActive: 'true', type: 'STORE_SUPPLIER' }
      });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Failed to load suppliers', error);
    }
  };

  const filteredRequests = useMemo(() => {
    return reorderVisibleRequests.filter((request) => {
      const matchesSearch =
        !filters.search ||
        request.partNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
        request.description?.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = filters.status === 'ALL' || request.status === filters.status;
      const matchesUrgency = filters.urgency === 'ALL' || request.urgency === filters.urgency;
      return matchesSearch && matchesStatus && matchesUrgency;
    });
  }, [reorderVisibleRequests, filters]);

  const filteredIndependentRequests = useMemo(() => {
    return independentVisibleRequests.filter((request) => {
      const matchesSearch =
        !independentFilters.search ||
        request.partNumber?.toLowerCase().includes(independentFilters.search.toLowerCase()) ||
        request.description?.toLowerCase().includes(independentFilters.search.toLowerCase()) ||
        `${request.createdBy?.firstName || ''} ${request.createdBy?.lastName || ''}`.toLowerCase().includes(independentFilters.search.toLowerCase());
      const matchesStatus = independentFilters.status === 'ALL' || request.status === independentFilters.status;
      const matchesUrgency = independentFilters.urgency === 'ALL' || request.urgency === independentFilters.urgency;
      return matchesSearch && matchesStatus && matchesUrgency;
    });
  }, [independentVisibleRequests, independentFilters]);

  const handleProcurementTabChange = (_event, nextTab) => {
    setSelectedRequestIds([]);
    setProcurementTab(nextTab);
  };

  const formatRequesterName = (createdBy) => {
    if (!createdBy) return '—';
    const name = `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim();
    return name || createdBy.email || '—';
  };

  const handleStatusChange = async (requestId, status) => {
    if (!status) return;
    setUpdatingRequestId(requestId);
    const previousRequests = requests;
    setRequests((prev) =>
      prev.map((request) =>
        request._id === requestId ? { ...request, status } : request
      )
    );
    try {
      const response = await axios.put(`/api/store/reorder-requests/${requestId}`, { status });
      setRequests((prev) =>
        prev.map((request) =>
          request._id === requestId ? response.data : request
        )
      );
      showToast('Request updated', 'success');
    } catch (error) {
      console.error('Failed to update request', error);
      setRequests(previousRequests);
      showToast(getErrorMessage(error, 'Failed to update request.'), 'error');
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const applyInventoryAutofill = (inventoryRow) => {
    const fields = inventoryAutofillFields(inventoryRow);
    setFormData((prev) => ({
      ...prev,
      partNumber: fields.partNumber || prev.partNumber,
      description: fields.description || prev.description,
      supplier: fields.supplier || prev.supplier
    }));
    if (inventoryRow?._id) {
      setSelectedInventoryItemId(inventoryRow._id);
    }
  };

  const handleInventorySelect = (event, value) => {
    if (value) {
      applyInventoryAutofill(value);
    } else {
      setSelectedInventoryItemId(null);
    }
  };

  const handlePartNumberBlur = async () => {
    const typed = normalizePartNumber(inventorySearchInput || formData.partNumber);
    if (!typed) return;
    if (selectedInventoryItemId) {
      const selected = inventoryOptions.find((item) => String(item._id) === String(selectedInventoryItemId));
      if (selected && normalizePartNumber(selected.partNumber).toLowerCase() === typed.toLowerCase()) {
        return;
      }
    }
    try {
      const match = await fetchInventoryByPartNumber(typed);
      if (match) {
        applyInventoryAutofill(match);
      } else {
        setFormData((prev) => ({ ...prev, partNumber: typed }));
        setSelectedInventoryItemId(null);
      }
    } catch {
      setFormData((prev) => ({ ...prev, partNumber: typed }));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedInventoryItemId(null);
    handleInventoryInputChange(null, '', 'clear');
    setFormData({
      partNumber: '',
      description: '',
      requestedQuantity: 1,
      urgency: 'MEDIUM',
      supplier: '',
      linkedMaintenanceId: '',
      notes: ''
    });
  };

  const handleCreate = async () => {
    const partNumber = normalizePartNumber(formData.partNumber);
    if (!partNumber || !formData.requestedQuantity) {
      showToast('Part number and quantity are required', 'error');
      return;
    }
    try {
      const payload = { ...formData, partNumber };
      if (!payload.supplier) delete payload.supplier;
      if (!payload.linkedMaintenanceId) delete payload.linkedMaintenanceId;
      if (!payload.notes) delete payload.notes;
      if (!payload.description) delete payload.description;
      await axios.post('/api/store/reorder-requests', payload);
      showToast('Reorder request created', 'success');
      handleDialogClose();
      fetchRequests();
    } catch (error) {
      console.error('Failed to create request', error);
      showToast(getErrorMessage(error, 'Failed to create request.'), 'error');
    }
  };

  return (
    <Box>
      <Tabs value={procurementTab} onChange={handleProcurementTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Reorder Requests" />
        <Tab label="Independent PO" />
        <Tab label="WO Requirement" />
        <Tab label="Purchase Orders" />
      </Tabs>

      {procurementTab === TAB_REORDER && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Reorder Requests</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchRequests}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreatePo && (
                <Button
                  variant="outlined"
                  disabled={!canCreatePoFromSelection}
                  onClick={() => setCreatePoFromRequestsOpen(true)}
                >
                  Create Purchase Order ({selectedRequestIds.length} selected)
                </Button>
              )}
              {canManage && (
                <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
                  New Request
                </Button>
              )}
            </Box>
          </Box>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Search by Part/Description"
                  size="small"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    value={filters.urgency}
                    label="Urgency"
                    onChange={(e) => setFilters({ ...filters, urgency: e.target.value })}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    {URGENCY_OPTIONS.map((urgency) => (
                      <MenuItem key={urgency} value={urgency}>
                        {urgency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canCreatePo && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedRequestIds.length > 0 && selectedRequestIds.length < filteredRequests.length}
                        checked={filteredRequests.length > 0 && selectedRequestIds.length === filteredRequests.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRequestIds(filteredRequests.map((r) => r._id));
                          } else {
                            setSelectedRequestIds([]);
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Part Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Urgency</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Linked WO</TableCell>
                  <TableCell>Created</TableCell>
                  {canManage && <TableCell>Update</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManage ? (canCreatePo ? 11 : 10) : (canCreatePo ? 10 : 9)} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((request) => (
                    <TableRow key={request._id}>
                      {canCreatePo && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRequestIds.includes(request._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRequestIds((prev) => [...prev, request._id]);
                              } else {
                                setSelectedRequestIds((prev) => prev.filter((id) => id !== request._id));
                              }
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {request.partNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{request.description || '—'}</TableCell>
                      <TableCell>{request.requestedQuantity}</TableCell>
                      <TableCell>
                        <Chip
                          label={request.urgency}
                          size="small"
                          color={
                            request.urgency === 'CRITICAL'
                              ? 'error'
                              : request.urgency === 'HIGH'
                              ? 'warning'
                              : request.urgency === 'MEDIUM'
                              ? 'info'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          size="small"
                          color={
                            request.status === 'DELIVERED'
                              ? 'success'
                              : request.status === 'CANCELLED'
                              ? 'default'
                              : 'primary'
                          }
                        />
                      </TableCell>
                      <TableCell>{request.supplier?.name || '—'}</TableCell>
                      <TableCell>
                        {request.requestedAircraftLabel || request.requestedAircraftId?.registration || 'GEN'}
                      </TableCell>
                      <TableCell>{request.linkedMaintenanceId?.title || request.linkedMaintenanceId || '—'}</TableCell>
                      <TableCell>
                        {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={request.status}
                              onChange={(e) => handleStatusChange(request._id, e.target.value)}
                              disabled={updatingRequestId === request._id}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </>
      )}

      {procurementTab === TAB_INDEPENDENT_PO && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">Independent PO</Typography>
              <Typography variant="body2" color="text.secondary">
                Planning raises requests without a work order. After AM approval, create a PO the same way as
                WO Requirement — open the PO form with lines prefilled and choose supplier there. Newest first.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchRequests}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreatePo && (
                <Tooltip
                  title={
                    !canCreateIndependentPoFromSelection
                      ? 'Select one or more AM-approved requests to create a PO.'
                      : ''
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={!canCreateIndependentPoFromSelection}
                      onClick={() => handleCreateIndependentPo(selectedIndependentRequests)}
                    >
                      Create PO ({selectedIndependentRequests.length} selected)
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Search by Part/Description/Requester"
                  size="small"
                  value={independentFilters.search}
                  onChange={(e) => setIndependentFilters({ ...independentFilters, search: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={independentFilters.status}
                    label="Status"
                    onChange={(e) => setIndependentFilters({ ...independentFilters, status: e.target.value })}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgency</InputLabel>
                  <Select
                    value={independentFilters.urgency}
                    label="Urgency"
                    onChange={(e) => setIndependentFilters({ ...independentFilters, urgency: e.target.value })}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    {URGENCY_OPTIONS.map((urgency) => (
                      <MenuItem key={urgency} value={urgency}>
                        {urgency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canCreatePo && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedRequestIds.length > 0 &&
                          selectedRequestIds.length < filteredIndependentRequests.filter((r) => r.status === 'APPROVED').length
                        }
                        checked={
                          filteredIndependentRequests.some((r) => r.status === 'APPROVED') &&
                          filteredIndependentRequests
                            .filter((r) => r.status === 'APPROVED')
                            .every((r) => selectedRequestIds.includes(r._id))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRequestIds(
                              filteredIndependentRequests
                                .filter((r) => r.status === 'APPROVED')
                                .map((r) => r._id)
                            );
                          } else {
                            setSelectedRequestIds([]);
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Part Number</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Urgency</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIndependentRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canCreatePo ? 11 : 10} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No independent PO requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIndependentRequests.map((request) => {
                    const isApproved = request.status === 'APPROVED';
                    const canPoForRow = canCreatePo && isApproved;
                    return (
                      <TableRow key={request._id}>
                        {canCreatePo && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedRequestIds.includes(request._id)}
                              disabled={!isApproved}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRequestIds((prev) => [...prev, request._id]);
                                } else {
                                  setSelectedRequestIds((prev) => prev.filter((id) => id !== request._id));
                                }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {request.partNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{request.description || '—'}</TableCell>
                        <TableCell>{request.requestedQuantity}</TableCell>
                        <TableCell>
                          <Chip
                            label={request.urgency}
                            size="small"
                            color={
                              request.urgency === 'CRITICAL'
                                ? 'error'
                                : request.urgency === 'HIGH'
                                ? 'warning'
                                : request.urgency === 'MEDIUM'
                                ? 'info'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.status}
                            size="small"
                            color={
                              request.status === 'DELIVERED'
                                ? 'success'
                                : request.status === 'APPROVED'
                                ? 'success'
                                : request.status === 'CANCELLED'
                                ? 'default'
                                : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {request.requestedAircraftLabel || request.requestedAircraftId?.registration || 'GEN'}
                        </TableCell>
                        <TableCell>{formatRequesterName(request.createdBy)}</TableCell>
                        <TableCell>{request.notes || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {request.createdAt ? new Date(request.createdAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="column" spacing={1} alignItems="flex-end">
                            <Button
                              component={RouterLink}
                              to="/planning"
                              size="small"
                              variant="outlined"
                            >
                              Open in Planning
                            </Button>
                            <Tooltip
                              title={
                                !canCreatePo
                                  ? 'You do not have permission to create purchase orders.'
                                  : !isApproved
                                  ? 'Awaiting AM approval before a PO can be created.'
                                  : ''
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  disabled={!canPoForRow}
                                  onClick={() => handleCreateIndependentPo(request)}
                                >
                                  Create PO
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                !canCreatePo
                                  ? 'You do not have permission to add purchase orders.'
                                  : !isApproved
                                  ? 'Awaiting AM approval before a PO can be created.'
                                  : ''
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  disabled={!canPoForRow}
                                  onClick={() => handleCreateIndependentPo(request)}
                                >
                                  Add PO
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {procurementTab === TAB_WO_REQUIREMENT && <StoreWorkOrderRequests />}

      <Dialog open={createPoFromRequestsOpen} onClose={() => setCreatePoFromRequestsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Purchase Order from Selected Requests</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedRequestIds.length} request(s) selected. All must have the same supplier.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={createPoFromRequestsPayload.operatorId}
                  label="Operator"
                  onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, operatorId: e.target.value }))}
                >
                  <MenuItem value="">Select</MenuItem>
                  {operators.map((o) => (
                    <MenuItem key={o._id} value={o._id}>{o.name} ({o.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Aircraft</InputLabel>
                <Select
                  value={createPoFromRequestsPayload.aircraftId}
                  label="Aircraft"
                  onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, aircraftId: e.target.value }))}
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="GEN">GEN (general stock)</MenuItem>
                  {aircraft.map((a) => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.registration} {a.model ? `– ${a.model}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Bill To</InputLabel>
                <Select
                  value={createPoFromRequestsPayload.billTo}
                  label="Bill To"
                  onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, billTo: e.target.value }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {billToAgencies.map((a) => (
                    <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Ship To</InputLabel>
                <Select
                  value={createPoFromRequestsPayload.shipTo}
                  label="Ship To"
                  onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, shipTo: e.target.value }))}
                >
                  <MenuItem value="">None</MenuItem>
                  {shipToAgencies.map((a) => (
                    <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label={displayTimezone ? `Expected Delivery (${getTimezoneLabel(displayTimezone)})` : 'Expected Delivery'}
                value={createPoFromRequestsPayload.expectedDeliveryDate}
                onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, expectedDeliveryDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={createPoFromRequestsPayload.priority || 'MEDIUM'}
                  label="Priority"
                  onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, priority: e.target.value }))}
                >
                  {PO_PRIORITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={createPoFromRequestsPayload.notes}
                onChange={(e) => setCreatePoFromRequestsPayload((p) => ({ ...p, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePoFromRequestsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreatePoFromRequests}
            disabled={!createPoFromRequestsPayload.operatorId || !createPoFromRequestsPayload.aircraftId || creatingPoFromRequests}
          >
            {creatingPoFromRequests ? 'Creating…' : 'Create Purchase Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {procurementTab === TAB_PURCHASE_ORDERS && (
        <>
          {poView === 'list' && (
            <PurchaseOrderList
              displayTimezone={displayTimezone}
              filters={poListFilters}
              onFiltersChange={setPoListFilters}
              onView={(id) => {
                setSelectedPoId(id);
                setPoView('detail');
              }}
              onEdit={(id) => {
                setSelectedPoId(id);
                setPoPrefill(null);
                setPoView('form');
              }}
              onCreate={() => {
                setSelectedPoId(null);
                setPoPrefill(null);
                setPoFormSessionKey((k) => k + 1);
                setPoView('form');
              }}
            />
          )}
          {poView === 'detail' && selectedPoId && (
            <PurchaseOrderDetail
              poId={selectedPoId}
              displayTimezone={displayTimezone}
              onBack={() => {
                setPoView('list');
                setSelectedPoId(null);
              }}
              onDeleted={() => {
                setPoView('list');
                setSelectedPoId(null);
              }}
              onEdit={(id) => {
                setSelectedPoId(id);
                setPoPrefill(null);
                setPoView('form');
              }}
            />
          )}
          {poView === 'form' && (
            <PurchaseOrderForm
              key={selectedPoId ? String(selectedPoId) : `new-${poFormSessionKey}`}
              poId={selectedPoId}
              initialData={selectedPoId ? null : poPrefill}
              displayTimezone={displayTimezone}
              onSaved={() => {
                setPoView('list');
                setSelectedPoId(null);
                setPoPrefill(null);
              }}
              onCancel={() => {
                setPoPrefill(null);
                if (selectedPoId) {
                  setPoView('detail');
                } else {
                  setPoView('list');
                  setSelectedPoId(null);
                }
              }}
            />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>New Reorder Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={inventoryOptions}
                loading={inventoryLoading}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return inventoryOptionLabel(option);
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option?._id || '') === String(value?._id || '')
                }
                value={
                  inventoryOptions.find((item) => String(item._id) === String(selectedInventoryItemId))
                  || (formData.partNumber ? formData.partNumber : null)
                }
                inputValue={inventorySearchInput}
                onInputChange={(event, value, reason) => {
                  handleInventoryInputChange(event, value, reason);
                  if (reason === 'input') {
                    setFormData((prev) => ({ ...prev, partNumber: value }));
                    if (!value.trim()) {
                      setSelectedInventoryItemId(null);
                    }
                  }
                }}
                onChange={handleInventorySelect}
                filterOptions={(options) => options}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Part Number"
                    required
                    placeholder="Search inventory by part number..."
                    onBlur={(event) => {
                      if (typeof params.inputProps?.onBlur === 'function') {
                        params.inputProps.onBlur(event);
                      }
                      handlePartNumberBlur();
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={formData.requestedQuantity}
                onChange={(e) =>
                  setFormData({ ...formData, requestedQuantity: parseInt(e.target.value) || 1 })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={formData.urgency}
                  label="Urgency"
                  onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                >
                  {URGENCY_OPTIONS.map((urgency) => (
                    <MenuItem key={urgency} value={urgency}>
                      {urgency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Supplier (optional)</InputLabel>
                <Select
                  value={formData.supplier}
                  label="Supplier (optional)"
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier._id} value={supplier._id}>
                      {supplier.name} ({supplier.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Linked Maintenance WO ID"
                placeholder="Optional"
                value={formData.linkedMaintenanceId}
                onChange={(e) => setFormData({ ...formData, linkedMaintenanceId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Create Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcurementModule;

